import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { X, Award, BookOpen, Star, Users } from 'lucide-react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useBusiness } from '../contexts/BusinessContext';
import { Loading } from '../components/ui/Loading';

interface Instructor {
  id: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  specialties: string[];
  certifications: string[];
  bio?: string;
  status: 'active' | 'inactive';
  classTypeIds?: string[]; // IDs de las clases que imparte
}

interface ClassType {
  id: string;
  name: string;
  color?: string;
  instructorIds?: string[];
}

const InstructorsShowcasePage: React.FC = () => {
  const { businessId } = useBusiness();
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [classTypes, setClassTypes] = useState<ClassType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInstructor, setSelectedInstructor] = useState<Instructor | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!businessId) {
        setLoading(false);
        return;
      }

      try {
        // Cargar instructores del negocio
        const instructorsQuery = query(
          collection(db, 'instructors'),
          where('businessId', '==', businessId)
        );
        const instructorsSnapshot = await getDocs(instructorsQuery);
        // Filtrar solo los activos (compatibilidad con status o isActive)
        const instructorsData = instructorsSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }) as Instructor & { isActive?: boolean })
          .filter(instructor => 
            instructor.status === 'active' || instructor.isActive === true
          );

        // Cargar tipos de clase
        const classTypesQuery = query(
          collection(db, 'classTypes'),
          where('businessId', '==', businessId)
        );
        const classTypesSnapshot = await getDocs(classTypesQuery);
        const classTypesData = classTypesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as ClassType[];

        setInstructors(instructorsData);
        setClassTypes(classTypesData);
      } catch (error) {
        console.error('Error loading instructors:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [businessId]);

  // Obtener las clases asignadas a un instructor (desde classTypeIds del instructor o instructorIds del classType)
  const getInstructorClasses = (instructor: Instructor) => {
    // Primero buscar por classTypeIds del instructor (nuevo campo)
    if (instructor.classTypeIds && instructor.classTypeIds.length > 0) {
      return classTypes.filter(ct => instructor.classTypeIds?.includes(ct.id));
    }
    // Fallback: buscar por instructorIds del classType (campo antiguo)
    return classTypes.filter(ct => ct.instructorIds?.includes(instructor.id));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loading size="lg" className="mx-auto mb-4" />
          <p className="text-gray-400">Cargando instructores...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Nuestros Instructores</title>
      </Helmet>

      <div className="min-h-screen">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-amber-400 mb-4">
            Nuestros Instructores
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Conoce a los profesionales que te guiarán en tu entrenamiento
          </p>
        </div>

        {/* Grid de instructores */}
        {instructors.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              No hay instructores disponibles en este momento
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 max-w-7xl mx-auto px-4">
            {instructors.map((instructor) => (
              <div
                key={instructor.id}
                onClick={() => setSelectedInstructor(instructor)}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                  {/* Imagen */}
                  <div className="aspect-[3/4] overflow-hidden">
                    {instructor.avatar ? (
                      <img
                        src={instructor.avatar}
                        alt={instructor.firstName}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-400 to-primary-600">
                        <span className="text-8xl font-bold text-white/80">
                          {instructor.firstName.charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Overlay con nombre */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6">
                    <h3 className="text-2xl font-bold text-white text-center">
                      {instructor.firstName}
                    </h3>
                    {instructor.specialties && instructor.specialties.length > 0 && (
                      <p className="text-primary-300 text-center text-sm mt-1">
                        {instructor.specialties.slice(0, 2).join(' · ')}
                      </p>
                    )}
                  </div>

                  {/* Indicador de "ver más" */}
                  <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Star className="w-5 h-5 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal de detalle del instructor */}
        {selectedInstructor && (
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedInstructor(null)}
          >
            <div 
              className="bg-white dark:bg-gray-800 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header con imagen */}
              <div className="relative h-72 sm:h-96">
                {selectedInstructor.avatar ? (
                  <img
                    src={selectedInstructor.avatar}
                    alt={selectedInstructor.firstName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-400 to-primary-600">
                    <span className="text-9xl font-bold text-white/80">
                      {selectedInstructor.firstName.charAt(0)}
                    </span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                
                {/* Botón cerrar */}
                <button
                  onClick={() => setSelectedInstructor(null)}
                  className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm hover:bg-white/40 rounded-full p-2 transition-colors"
                >
                  <X className="w-6 h-6 text-white" />
                </button>

                {/* Nombre sobre la imagen */}
                <div className="absolute bottom-6 left-6 right-6">
                  <h2 className="text-4xl font-bold text-white">
                    {selectedInstructor.firstName}
                  </h2>
                </div>
              </div>

              {/* Contenido */}
              <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-24rem)]">
                {/* Especialidades */}
                {selectedInstructor.specialties && selectedInstructor.specialties.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Award className="w-5 h-5 text-primary-500" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Especialidades
                      </h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedInstructor.specialties.map((specialty, index) => (
                        <span
                          key={index}
                          className="px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Certificaciones */}
                {selectedInstructor.certifications && selectedInstructor.certifications.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Star className="w-5 h-5 text-yellow-500" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Certificaciones
                      </h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedInstructor.certifications.map((cert, index) => (
                        <span
                          key={index}
                          className="px-4 py-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-full text-sm font-medium"
                        >
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Clases asignadas */}
                {getInstructorClasses(selectedInstructor).length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <BookOpen className="w-5 h-5 text-green-500" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Clases que imparte
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {getInstructorClasses(selectedInstructor).map((classType) => (
                        <div
                          key={classType.id}
                          className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl"
                        >
                          <p className="font-medium text-green-800 dark:text-green-300">
                            {classType.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bio si existe */}
                {selectedInstructor.bio && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Sobre {selectedInstructor.firstName}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                      {selectedInstructor.bio}
                    </p>
                  </div>
                )}

                {/* Si no hay ningún dato extra */}
                {(!selectedInstructor.specialties || selectedInstructor.specialties.length === 0) &&
                 (!selectedInstructor.certifications || selectedInstructor.certifications.length === 0) &&
                 getInstructorClasses(selectedInstructor).length === 0 &&
                 !selectedInstructor.bio && (
                  <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400">
                      Pronto más información sobre {selectedInstructor.firstName}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default InstructorsShowcasePage;

